import { Form, Head, usePage } from "@inertiajs/react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Invite {
  id: number
  org_name: string
  email: string
  url: string
  opened: boolean
  opened_at: string | null
  created_at: string
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Outreach demos", href: "/admin/outreach" },
]

export default function OutreachInvites() {
  const { invites } = usePage<{ invites: Invite[] }>().props
  const [copiedId, setCopiedId] = useState<number | null>(null)

  function copyLink(invite: Invite) {
    navigator.clipboard.writeText(invite.url)
    setCopiedId(invite.id)
    setTimeout(() => setCopiedId(null), 1500)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Outreach demos" />

      <div className="space-y-8 p-4">
        {/* /admin/outreach is a fixed superadmin route, not org-scoped. */}
        <Form method="post" action="/admin/outreach" className="space-y-4">
          {({ processing }) => (
            <>
              <div>
                <h2 className="text-lg font-medium">Generate demo links</h2>
                <p className="text-muted-foreground text-sm">
                  One prospect below, or paste a CSV (
                  <code>Org name,email</code> per row) for bulk.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="org_name">Org name</Label>
                  <Input
                    id="org_name"
                    name="org_name"
                    placeholder="Acme Transload"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="email">Contact email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="ops@acme.com"
                  />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="csv">
                  …or bulk paste (CSV: Org name,email)
                </Label>
                <Textarea
                  id="csv"
                  name="csv"
                  rows={4}
                  placeholder={
                    "Acme Transload,ops@acme.com\nBeta Rail,jane@beta.com"
                  }
                />
              </div>

              <Button type="submit" disabled={processing}>
                Create links
              </Button>
            </>
          )}
        </Form>

        <div className="space-y-3">
          <h2 className="text-lg font-medium">Prospects ({invites.length})</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Link</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map((invite) => (
                <TableRow key={invite.id}>
                  <TableCell className="font-medium">
                    {invite.org_name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {invite.email}
                  </TableCell>
                  <TableCell>
                    {invite.opened ? (
                      <Badge variant="secondary">Opened</Badge>
                    ) : (
                      <Badge variant="outline">Sent</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyLink(invite)}
                    >
                      {copiedId === invite.id ? "Copied!" : "Copy link"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {invites.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-muted-foreground text-center text-sm"
                  >
                    No prospects yet — add one above.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppLayout>
  )
}
